import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { http } from '@/lib/api';
import {
  Search, X, ArrowRight, Sparkles, BookOpen, TrendingUp, HandHeart,
  Feather, MessageCircleHeart, Compass, ClipboardList, Users, LifeBuoy, HelpCircle,
  Home as HomeIcon, User, Loader2, History, Zap,
} from 'lucide-react';
import VoiceInputButton from '@/components/VoiceInputButton';

const ICON_MAP = {
  BookOpen, TrendingUp, HandHeart, Feather, MessageCircleHeart,
  Compass, ClipboardList, Users, LifeBuoy, HelpCircle, Home: HomeIcon, User, Sparkles,
};

const PLACEHOLDERS = [
  'Search Brotherhood\u2026',
  'Ask Brotherhood\u2026',
  'Find a lesson\u2026',
  'Find a conversation\u2026',
  'Search anything\u2026',
  'Need help with something?',
];

const FILTERS = [
  { id: 'all', label: 'Everything' },
  { id: 'lessons', label: 'Lessons' },
  { id: 'coach', label: 'Coach' },
  { id: 'journal', label: 'Journal' },
  { id: 'plans', label: 'Plans' },
  { id: 'support', label: 'Support' },
  { id: 'pages', label: 'Pages' },
];

const TYPE_LABEL = {
  lesson: 'LESSON',
  growth_plan: 'PLAN', support_guide: 'SUPPORT',
  journal: 'JOURNAL', coach_message: 'AI COACH', page: 'JUMP TO',
  crisis: 'CRISIS',
};

const TYPE_ACCENT = {
  lesson: '#0B4F6C',
  growth_plan: '#1E7A54', support_guide: '#B45309',
  journal: '#78350F', coach_message: '#0B192C', page: '#334155',
  crisis: '#B91C1C',
};

function ResultIcon({ name, className = '' }) {
  const Cmp = ICON_MAP[name] || Sparkles;
  return <Cmp size={18} strokeWidth={1.75} className={className} />;
}

export default function GlobalSearch() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [results, setResults] = useState([]);
  const [aiResult, setAiResult] = useState(null); // {message, picks}
  const [aiLoading, setAiLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);
  const [recent, setRecent] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const listRef = useRef(null);

  // Rotating placeholder
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => setPlaceholderIdx(i => (i + 1) % PLACEHOLDERS.length), 2800);
    return () => clearInterval(t);
  }, [open]);

  // Global keyboard trigger
  useEffect(() => {
    const onKey = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdK = ((isMac && e.metaKey) || (!isMac && e.ctrlKey)) && e.key.toLowerCase() === 'k';
      if (cmdK) { e.preventDefault(); setOpen(o => !o); return; }
      if (e.key === '/' && !open) {
        const tag = (e.target?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
        e.preventDefault(); setOpen(true);
      }
      if (e.key === 'Escape' && open) { setOpen(false); }
    };
    window.addEventListener('keydown', onKey);
    // Custom event so TopNav/BottomNav buttons can open it
    const onOpen = () => setOpen(true);
    window.addEventListener('bp:open-search', onOpen);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('bp:open-search', onOpen); };
  }, [open]);

  // On open: focus, load recent + suggestions, lock body scroll
  useEffect(() => {
    if (!open) return;
    setTimeout(() => inputRef.current?.focus(), 60);
    document.body.style.overflow = 'hidden';
    http.get('/search/recent').then(r => setRecent(r.data.items || [])).catch(() => {});
    http.get('/search/suggestions').then(r => setSuggestions(r.data.items || [])).catch(() => {});
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q) { setResults([]); setAiResult(null); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await http.post('/search', { query: q, filter, limit: 30 });
        setResults(r.data.results || []);
        setActiveIdx(0);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 180);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, filter, open]);

  const runAI = useCallback(async (q) => {
    const text = (q || query).trim();
    if (!text) return;
    setAiLoading(true); setAiResult(null);
    try {
      const r = await http.post('/search/ai', { query: text });
      setAiResult(r.data);
    } catch (e) {
      setAiResult({ message: "I couldn't find an exact match \u2014 but here's what's related below.", picks: [] });
    } finally { setAiLoading(false); }
  }, [query]);

  const openResult = (r) => {
    setOpen(false);
    if (r.url?.startsWith('tel:')) { window.location.href = r.url; return; }
    nav(r.url);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      if (results[activeIdx]) { e.preventDefault(); openResult(results[activeIdx]); }
      else if (query.trim()) { runAI(); }
    }
  };

  const runSuggestion = (q) => { setQuery(q); setActiveIdx(0); };

  const hasQuery = query.trim().length > 0;

  if (!open) return null;

  return (
    <div
      data-testid="global-search-overlay"
      className="fixed inset-0 z-[70] flex items-start justify-center pt-[8vh] px-4 animate-fade-in"
      style={{ backgroundColor: 'rgba(11,25,44,0.72)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' }}
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden animate-scale-in"
        style={{ boxShadow: '0 25px 80px -20px rgba(11,25,44,0.4)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="global-search-panel"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-navy/5">
          <Search size={20} className="text-navy/40 shrink-0" strokeWidth={1.75} />
          <input
            ref={inputRef}
            data-testid="global-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={PLACEHOLDERS[placeholderIdx]}
            className="flex-1 bg-transparent border-0 outline-none text-lg text-navy placeholder-navy/40"
          />
          {loading && <Loader2 size={16} className="animate-spin text-navy/40" />}
          <VoiceInputButton
            compact
            testid="global-search-voice-btn"
            ariaLabel="Dictate your search"
            onTranscript={(chunk) => setQuery(prev => (prev + ' ' + chunk).trim())}
          />
          <button
            data-testid="global-search-close"
            onClick={() => setOpen(false)}
            className="p-1.5 rounded-full hover:bg-beige transition-smooth"
            aria-label="Close search"
          >
            <X size={18} className="text-navy/60" strokeWidth={1.75} />
          </button>
        </div>

        {/* Filter chips */}
        <div className="flex gap-1.5 px-5 py-3 border-b border-navy/5 overflow-x-auto scrollbar-none" data-testid="global-search-filters">
          {FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`global-search-filter-${f.id}`}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-smooth ${
                filter === f.id ? 'bg-navy text-white' : 'bg-beige/70 text-navy/70 hover:bg-beige'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto">
          {/* Empty state — recent + suggestions */}
          {!hasQuery && (
            <div className="p-5 space-y-6" data-testid="global-search-empty">
              {recent.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-navy/50 font-semibold mb-2 flex items-center gap-1.5">
                    <History size={11} /> Recent
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {recent.map((r) => (
                      <button
                        key={r.display}
                        onClick={() => runSuggestion(r.display)}
                        data-testid={`global-search-recent-${r.display.slice(0,20)}`}
                        className="px-3 py-1.5 rounded-full text-sm bg-beige/70 hover:bg-beige text-navy/80 transition-smooth"
                      >
                        {r.display}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {suggestions.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-navy/50 font-semibold mb-2 flex items-center gap-1.5">
                    <Zap size={11} className="text-gold" /> Try one of these
                  </p>
                  <div className="space-y-1">
                    {suggestions.map((s, i) => (
                      <button
                        key={s.label}
                        onClick={() => runSuggestion(s.query)}
                        data-testid={`global-search-suggestion-${i}`}
                        className="w-full flex items-center justify-between text-left px-3 py-2.5 rounded-2xl hover:bg-beige/60 text-navy transition-smooth group"
                      >
                        <span className="text-sm">{s.label}</span>
                        <ArrowRight size={14} className="text-navy/30 group-hover:text-gold transition-smooth" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-[11px] text-navy/40 text-center pt-2">
                Tip: press <kbd className="px-1.5 py-0.5 rounded bg-beige border border-navy/10 font-mono text-[10px]">⌘K</kbd> or <kbd className="px-1.5 py-0.5 rounded bg-beige border border-navy/10 font-mono text-[10px]">/</kbd> anywhere
              </div>
            </div>
          )}

          {/* Results list */}
          {hasQuery && results.length > 0 && (
            <div className="py-2" data-testid="global-search-results">
              {results.map((r, i) => (
                <button
                  key={`${r.type}-${r.id}-${i}`}
                  onClick={() => openResult(r)}
                  onMouseEnter={() => setActiveIdx(i)}
                  data-testid={`global-search-result-${i}`}
                  className={`w-full text-left px-5 py-3 flex items-start gap-4 transition-smooth ${
                    activeIdx === i ? 'bg-beige/70' : 'hover:bg-beige/40'
                  }`}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${TYPE_ACCENT[r.type] || '#0B192C'}18`, color: TYPE_ACCENT[r.type] || '#0B192C' }}
                  >
                    <ResultIcon name={r.icon} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[9px] uppercase tracking-[0.22em] font-semibold" style={{ color: TYPE_ACCENT[r.type] || '#0B192C' }}>
                        {TYPE_LABEL[r.type] || r.type.toUpperCase()}
                      </span>
                      {r.duration && <span className="text-[10px] text-navy/40">· {r.duration}</span>}
                      {r.premium && <span className="text-[9px] uppercase tracking-widest text-gold font-semibold">· Members</span>}
                    </div>
                    <p className="font-heading text-base text-navy leading-snug truncate">{r.title}</p>
                    {r.subtitle && <p className="text-xs text-navy/55 mt-1 line-clamp-2">{r.subtitle}</p>}
                    {r.why_matched && (
                      <p className="text-[11px] text-navy/50 mt-1.5 italic">— {r.why_matched}</p>
                    )}
                  </div>
                  <ArrowRight size={14} className="text-navy/30 mt-3 shrink-0" />
                </button>
              ))}
            </div>
          )}

          {/* No exact matches — never say "no results" */}
          {hasQuery && !loading && results.length === 0 && (
            <div className="p-6 text-center" data-testid="global-search-no-results">
              <p className="font-heading text-lg text-navy">I couldn&apos;t find an exact match.</p>
              <p className="text-sm text-navy/60 mt-1">Ask Brotherhood might still help — tap below.</p>
            </div>
          )}

          {/* Ask Brotherhood AI */}
          {hasQuery && (
            <div className="border-t border-navy/5 bg-gradient-to-br from-[#0B192C] to-[#1a2f4c] text-white p-5" data-testid="global-search-ai-block">
              {!aiResult && !aiLoading && (
                <button
                  onClick={() => runAI()}
                  data-testid="global-search-ai-run"
                  className="w-full flex items-center justify-between gap-3 group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(197,160,89,0.2)' }}>
                      <Sparkles size={18} className="text-gold" strokeWidth={1.75} />
                    </div>
                    <div className="text-left">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Ask Brotherhood AI</p>
                      <p className="text-sm mt-0.5">Recommend content for &ldquo;{query.slice(0, 40)}&rdquo;</p>
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-white/60 group-hover:translate-x-0.5 transition-smooth" />
                </button>
              )}
              {aiLoading && (
                <div className="flex items-center gap-3 py-3">
                  <Loader2 size={16} className="animate-spin text-gold" />
                  <p className="text-sm text-white/80">Thinking…</p>
                </div>
              )}
              {aiResult && (
                <div data-testid="global-search-ai-result">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold mb-2 flex items-center gap-1.5">
                    <Sparkles size={11} /> Ask Brotherhood
                  </p>
                  <p className="text-sm text-white/90 leading-relaxed">{aiResult.message}</p>
                  {aiResult.picks?.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {aiResult.picks.map((p, i) => (
                        <button
                          key={`${p.type}-${p.id}-${i}`}
                          onClick={() => openResult(p)}
                          data-testid={`global-search-ai-pick-${i}`}
                          className="w-full text-left rounded-2xl p-3 flex items-start gap-3 hover:bg-white/5 transition-smooth border border-white/10"
                        >
                          <div
                            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                            style={{ backgroundColor: 'rgba(197,160,89,0.15)', color: '#C5A059' }}
                          >
                            <ResultIcon name={p.icon} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-[9px] uppercase tracking-[0.22em] text-gold font-semibold">{TYPE_LABEL[p.type] || p.type.toUpperCase()}</span>
                              {p.duration && <span className="text-[10px] text-white/50">· {p.duration}</span>}
                            </div>
                            <p className="font-heading text-sm text-white">{p.title}</p>
                            <p className="text-[11px] text-white/60 mt-1 italic">— {p.why_matched}</p>
                          </div>
                          <ArrowRight size={13} className="text-white/40 mt-3" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Trigger button to open GlobalSearch. Place in TopNav / BottomNav. */
export function GlobalSearchTrigger({ testid = 'global-search-open', compact = false }) {
  const open = () => window.dispatchEvent(new Event('bp:open-search'));
  if (compact) {
    return (
      <button
        onClick={open}
        data-testid={testid}
        aria-label="Search Brotherhood+"
        className="p-2 rounded-full text-navy/60 hover:text-navy hover:bg-beige transition-smooth"
      >
        <Search size={18} strokeWidth={1.75} />
      </button>
    );
  }
  return (
    <button
      onClick={open}
      data-testid={testid}
      className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs text-navy/60 bg-beige/70 hover:bg-beige border border-navy/5 transition-smooth min-w-[180px] justify-between"
    >
      <span className="inline-flex items-center gap-1.5">
        <Search size={14} strokeWidth={1.75} />
        <span>Search Brotherhood…</span>
      </span>
      <kbd className="px-1.5 py-0.5 rounded bg-white border border-navy/10 font-mono text-[10px] text-navy/50">⌘K</kbd>
    </button>
  );
}
